import React, { useState, useEffect, useRef } from 'react';
import Select, { components } from 'react-select';

// Custom Option Component for React-Select
const CustomOption = (props) => {
    const { data, selectProps } = props;

    if (data.isSeparator) {
        return (
            <div style={{ padding: '0 12px' }}>
                <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #E2E8F0' }} />
            </div>
        );
    }

    // Only render custom flex layout and Edit button for custom created options
    if (data.isCustomCreated) {
        const onEditClick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            if (selectProps.onEditCustomOption) {
                selectProps.onEditCustomOption(data);
            }
        };

        return (
            <components.Option {...props}>
                <div className="df aic jcsb fullWidth" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>{data.label}</span>
                    <span
                        onClick={onEditClick}
                        className="cursorPointer editBtn"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0 8px',
                            color: '#0466c8',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer'
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                            <path d="M11.194 0.246632C11.5224 -0.0822106 12.0563 -0.0822106 12.3847 0.246632L15.7531 3.61506C16.0823 3.9439 16.0823 4.47712 15.7531 4.80596L4.80583 15.7533C4.64836 15.9112 4.43363 16 4.21047 16H0.842094C0.377006 16 0 15.623 0 15.1579V11.7895C0 11.5661 0.0887568 11.3519 0.246649 11.194L8.66768 2.77312L11.194 0.246632ZM9.26304 4.55931L1.68419 12.1383V14.3158H3.86184L11.4407 6.73683L9.26304 4.55931ZM12.6314 5.54592L13.967 4.21051L11.7893 2.03299L10.4538 3.3684L12.6314 5.54592Z" fill="currentColor" />
                        </svg>
                        Edit
                    </span>
                </div>
            </components.Option>
        );
    }

    if (data.isOriginalOther) {
        return (
            <components.Option {...props}>
                <div className="df aic jcsb fullWidth" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span style={{ color: '#0466c8', fontWeight: '500' }}>{data.label}</span>
                </div>
            </components.Option>
        );
    }

    return <components.Option {...props} />;
};

// Custom SingleValue Component for React-Select
const CustomSingleValue = (props) => {
    const { data } = props;
    return (
        <components.SingleValue {...props}>
            {data.label}
        </components.SingleValue>
    );
};

function SelectWithCustomOption(props) {
    const {
        options = [],
        value,
        onChange,
        popupTitle = "Add a new Type",
        addNewOptionLabel = "Add new Category (+)",
        customValueDefault = "",
        incomeType,
        expenseType,
        activityCategory,
        specialtyType,
        popupPlaceholder,
        name,
        ...rest
    } = props;

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [customOptions, setCustomOptions] = useState([]);
    const [editingOption, setEditingOption] = useState(null);
    const [tempInputValue, setTempInputValue] = useState('');

    const initializedDefaults = useRef(new Set());

    // Find the original "Other" option in raw options
    const otherOpt = options.find(opt => opt.label?.toLowerCase() === 'other' || opt.value?.toLowerCase() === 'other');

    // Initialize customValueDefault exactly once when options are loaded
    useEffect(() => {
        if (customValueDefault && otherOpt && !initializedDefaults.current.has(customValueDefault)) {
            initializedDefaults.current.add(customValueDefault);
            const exists = customOptions.some(opt => opt.label?.toLowerCase() === customValueDefault.toLowerCase());
            const newCustom = {
                id: otherOpt.id,
                label: customValueDefault,
                value: customValueDefault,
                isOther: true,
                isCustomCreated: true
            };

            if (!exists) {
                setCustomOptions(prev => [...prev, newCustom]);
            }

            // If current selected value is just the generic "Other" option,
            // automatically select the custom option.
            if (value && (value.label?.toLowerCase() === 'other' || value.value?.toLowerCase() === 'other')) {
                if (onChange) {
                    onChange(newCustom);
                }
            }
        }
    }, [customValueDefault, options, otherOpt, value, onChange, customOptions]);

    // Construct modified options list:
    // 1. All standard options except "Other"
    // 2. All custom created options
    // 3. The original "Other" option (renamed to "Add a new Type (+)") at the bottom
    const modifiedOptions = [];
    const standardOptions = [];
    const customCreatedOptions = [];

    options.forEach((opt) => {
        const isOther = opt.label?.toLowerCase() === 'other' || opt.value?.toLowerCase() === 'other';
        if (!isOther) {
            if (opt.isCustomCreated) {
                customCreatedOptions.push(opt);
            } else {
                standardOptions.push(opt);
            }
        }
    });

    modifiedOptions.push(...standardOptions);

    // Filter out unique custom created options
    const uniqueCustomOptions = [];
    customCreatedOptions.forEach(opt => {
        if (!uniqueCustomOptions.some(o => o.label?.toLowerCase() === opt.label?.toLowerCase()) &&
            !standardOptions.some(o => o.label?.toLowerCase() === opt.label?.toLowerCase())) {
            uniqueCustomOptions.push(opt);
        }
    });
    customOptions.forEach(cust => {
        if (!uniqueCustomOptions.some(o => o.label?.toLowerCase() === cust.label?.toLowerCase()) &&
            !standardOptions.some(o => o.label?.toLowerCase() === cust.label?.toLowerCase())) {
            uniqueCustomOptions.push(cust);
        }
    });

    if (standardOptions.length > 0 && uniqueCustomOptions.length > 0) {
        modifiedOptions.push({
            value: 'separator',
            label: 'separator',
            isSeparator: true,
            isDisabled: true
        });
    }

    modifiedOptions.push(...uniqueCustomOptions);

    if (otherOpt) {
        modifiedOptions.push({
            ...otherOpt,
            label: addNewOptionLabel,
            value: addNewOptionLabel,
            isOther: true,
            isOriginalOther: true
        });
    }

    // Handle React Select selection change
    const handleSelectChange = (selectedOption, actionMeta) => {
        if (selectedOption && selectedOption.isOriginalOther) {
            setEditingOption(null);
            setTempInputValue('');
            setIsPopupOpen(true);
        } else {
            if (onChange) {
                onChange(selectedOption, actionMeta);
            }
        }
    };

    // When the Edit button is clicked in the dropdown Option
    const handleEditCustomOption = (option) => {
        setEditingOption(option);
        setTempInputValue(option.label);
        setIsPopupOpen(true);
    };

    const handlePopupOk = async () => {
        const trimmed = tempInputValue.trim();
        if (trimmed) {
            setIsPopupOpen(false);

            if (editingOption) {
                if (props.onEditCustomOption) {
                    const result = await props.onEditCustomOption(editingOption, trimmed);
                    if (result && onChange) {
                        const updatedOpt = typeof result === 'object'
                            ? {
                                ...result,
                                label: result.label || result.title || trimmed,
                                value: result.value || result.title || trimmed,
                                isCustomCreated: true
                            }
                            : { ...editingOption, label: trimmed, value: trimmed };
                        setCustomOptions(prev => prev.map(opt =>
                            (String(opt.id) === String(editingOption.id) || opt.label?.toLowerCase() === editingOption.label?.toLowerCase()) ? updatedOpt : opt
                        ));
                        onChange(updatedOpt);
                    }
                } else {
                    // Editing an existing custom option
                    const updatedOption = {
                        ...editingOption,
                        label: trimmed,
                        value: trimmed
                    };

                    setCustomOptions(prev => prev.map(opt =>
                        opt.label === editingOption.label ? updatedOption : opt
                    ));

                    // Always select the updated custom option after editing.
                    if (onChange) {
                        onChange(updatedOption);
                    }
                }
            } else {
                if (props.onCreateCustomOption) {
                    const result = await props.onCreateCustomOption(trimmed);
                    if (result && onChange) {
                        const newOpt = typeof result === 'object'
                            ? {
                                ...result,
                                label: result.label || result.title || trimmed,
                                value: result.value || result.title || trimmed,
                                isCustomCreated: true
                            }
                            : { id: result, label: trimmed, value: trimmed, isCustomCreated: true };
                        setCustomOptions(prev => [...prev, newOpt]);
                        onChange(newOpt);
                    }
                } else {
                    // Creating a new custom option
                    // Check if an option with this label already exists (case-insensitive)
                    const existing = customOptions.find(opt => opt.label.toLowerCase() === trimmed.toLowerCase());
                    if (existing) {
                        if (onChange) {
                            onChange(existing);
                        }
                    } else if (otherOpt) {
                        const newCustom = {
                            id: otherOpt.id,
                            label: trimmed,
                            value: trimmed,
                            isOther: true,
                            isCustomCreated: true
                        };
                        setCustomOptions(prev => [...prev, newCustom]);
                        if (onChange) {
                            onChange(newCustom);
                        }
                    }
                }
            }
        }
    };

    const handlePopupCancel = () => {
        setIsPopupOpen(false);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handlePopupOk();
        }
    };

    // Find the correct selected value to match the parent's value prop
    let selectedValue = value || null;
    const activeSelectedState =
        name === 'incomeType' ? incomeType :
            name === 'expenseType' ? expenseType :
                name === 'activityCategory' ? activityCategory :
                    name === 'specialty' ? specialtyType :
                        (incomeType || expenseType || activityCategory || specialtyType);
    if (activeSelectedState && activeSelectedState.isCustomCreated) {
        selectedValue = modifiedOptions.find(o => o.isCustomCreated && o.label === activeSelectedState.label);
        if (!selectedValue) {
            selectedValue = activeSelectedState;
        }
    } else if (value) {
        // Match by reference, or matching ID and label (especially for custom options)
        const match = modifiedOptions.find(o =>
            o === value ||
            (String(o.id) === String(value.id) && o.label === value.label)
        );

        if (match) {
            selectedValue = match;
        } else if (String(value.id) === String(otherOpt?.id) || value.label?.toLowerCase() === 'other') {
            const originalOther = modifiedOptions.find(o => o.isOriginalOther);
            if (originalOther) {
                selectedValue = originalOther;
            }
        }
    }

    // Determine default placeholder based on popupTitle
    let defaultPlaceholder = "Enter Category name";
    if (popupTitle.toLowerCase().includes("specialty")) {
        defaultPlaceholder = "Enter Specialty name";
    } else if (popupTitle.toLowerCase().includes("type")) {
        defaultPlaceholder = "Enter Type name";
    }
    const resolvedPlaceholder = popupPlaceholder || defaultPlaceholder;

    // Determine edit title based on popupTitle
    let editTitle = "Edit Category";
    if (popupTitle.includes("Add a new ")) {
        editTitle = popupTitle.replace("Add a new ", "Edit ");
    } else if (popupTitle.includes("Add ")) {
        editTitle = popupTitle.replace("Add ", "Edit ");
    } else {
        editTitle = `Edit ${popupTitle}`;
    }

    const isSaveDisabled = editingOption
        ? (!tempInputValue.trim() || tempInputValue.trim() === editingOption.label.trim())
        : !tempInputValue.trim();


    return (
        <>
            <Select
                {...rest}
                options={modifiedOptions}
                value={selectedValue}
                onChange={handleSelectChange}
                onEditCustomOption={handleEditCustomOption}
                // menuIsOpen={true}
                // closeMenuOnSelect={false}
                components={{
                    Option: CustomOption,
                    SingleValue: CustomSingleValue
                }}
            />

            {/* Custom Premium Modal Popup */}
            {isPopupOpen && (
                <div className="popTop" style={{ animation: 'fadeIn 0.2s ease-out' }}>
                    <div className="popContainer popMaxWidth">
                        <div className="popMaster">
                            <div className="popUpMainBg fullWidth" style={{ padding: '30px' }}>
                                <div className="areYouText areYouDmFont" style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px', textAlign: 'center' }}>
                                    {editingOption ? editTitle : popupTitle}
                                </div>
                                <div className="fullWidth" style={{ marginBottom: '25px' }}>
                                    <input
                                        type="text"
                                        value={tempInputValue}
                                        onChange={(e) => setTempInputValue(e.target.value)}
                                        onKeyDown={handleInputKeyDown}
                                        placeholder={resolvedPlaceholder}
                                        className="fullWidth inputColum24size"
                                        style={{ height: '44px', padding: '0 12px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        autoFocus
                                    />
                                </div>
                                <div className="fullWidth texAlingCener df jcc aic" style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>

                                    <button
                                        type="button"
                                        className="popUpYesButton"
                                        onClick={handlePopupCancel}
                                        style={{ minWidth: '120px' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="popUpNoButton"
                                        onClick={handlePopupOk}
                                        disabled={isSaveDisabled}
                                        style={isSaveDisabled ? { opacity: 0.5, pointerEvents: 'none', minWidth: '120px' } : { minWidth: '120px' }}
                                    >
                                        SAVE
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default SelectWithCustomOption;
